import initCanvas from './canvas';
import initAccessories from './accessories';
import initUndo from './undo';
import initShareModal from './share';

document.addEventListener('DOMContentLoaded', () => {
	const wrapper = document.querySelector('.wp-block-mavis-design-a-hairstyle');
	if (!wrapper) return;

	const dom = {
		wrapper,
		canvas: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__canvas'),
		accessoryContainer: wrapper.querySelector(
			'.wp-block-mavis-design-a-hairstyle__accessory-container',
		),
		startPoint: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__start'),
		hairOption: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__game-option--hair'),
		colourOption: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__game-option--colour'),
		accessoriesOption: wrapper.querySelector(
			'.wp-block-mavis-design-a-hairstyle__game-option--accessories',
		),
		colourOptionsList: wrapper.querySelector(
			'.wp-block-mavis-design-a-hairstyle__game-colour-options',
		),
		colourItems: wrapper.querySelectorAll('.wp-block-mavis-design-a-hairstyle__game-colour-option'),
		accessoryButtons: wrapper.querySelectorAll(
			'.wp-block-mavis-design-a-hairstyle__accessories-list .wp-block-mavis-design-a-hairstyle__accessory img',
		),
		undoButton: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__undo-button'),
		clearButton: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__clear-button'),
		startOverButton: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__start-over-button'),
		shareButton: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__share-button'),
		optionsContainer: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__game-options'),
		headSelection: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__head-selection'),
		headChoices: wrapper.querySelectorAll('.wp-block-mavis-design-a-hairstyle__head-choice'),
		headContainer: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__head-container'),
		pointingHand: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__pointing-hand'),
		tutorialModal: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__tutorial-modal'),
		tutorialStartButton: wrapper.querySelector(
			'.wp-block-mavis-design-a-hairstyle__tutorial-start-button',
		),
		drawingOptionsList: wrapper.querySelector(
			'.wp-block-mavis-design-a-hairstyle__game-drawing-options',
		),
		drawingItems: wrapper.querySelectorAll(
			'.wp-block-mavis-design-a-hairstyle__game-drawing-option',
		),
	};

	const state = {
		currentMode: 'drawing',
		drawingColor: '#000000',
		selectedAccessory: null,
		drawingTile: '',
	};

	state.drawingTile = wrapper.dataset.drawingTile || wrapper.dataset.secondDrawingTile || '';

	/**
	 * Show the tutorial modal and call onComplete after it starts.
	 * @param {Function} onComplete
	 */
	const displayTutorial = (onComplete) => {
		if (!dom.tutorialModal || !dom.tutorialStartButton) {
			if (typeof onComplete === 'function') onComplete();
			return;
		}

		dom.tutorialModal.classList.add('is-visible');
		dom.tutorialStartButton.addEventListener(
			'click',
			() => {
				dom.tutorialModal.classList.remove('is-visible');
				if (typeof onComplete === 'function') onComplete();
			},
			{ once: true },
		);
	};

	/**
	 * Updates the cursor based on the current mode.
	 */
	const updateCursor = () => {
		if (!dom.canvas) return;
		if (state.currentMode === 'coloring') {
			dom.canvas.classList.add('coloring-cursor');
		} else {
			dom.canvas.classList.remove('coloring-cursor');
		}
	};

	/**
	 * Resets the game state to drawing mode.
	 */
	const resetToDrawingMode = () => {
		state.currentMode = 'drawing';
		updateCursor();
		state.selectedAccessory = null;

		dom.hairOption?.classList.add('is-active');
		dom.colourOption?.classList.remove('is-active');
		dom.accessoriesOption?.classList.remove('is-active');
		dom.colourOptionsList?.classList.add(
			'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
		);
		dom.drawingOptionsList?.classList.add(
			'wp-block-mavis-design-a-hairstyle__game-drawing-options--hidden',
		);

		dom.accessoryButtons?.forEach((b) => b.classList.remove('is-active'));
	};

	/**
	 * Check if the canvas or accessory container contains any content
	 * and toggle UI buttons (undo/clear/start/share) accordingly.
	 * @returns {boolean}
	 */
	const checkCanvasHasContent = () => {
		if (!dom.canvas) return false;

		let hasDrawing = false;
		try {
			const ctx = dom.canvas.getContext('2d');
			const pixels = ctx.getImageData(0, 0, dom.canvas.width, dom.canvas.height).data;
			for (let i = 0; i < pixels.length; i += 4) {
				if (pixels[i + 3] > 0) {
					hasDrawing = true;
					break;
				}
			}
		} catch (e) {
			hasDrawing = false;
		}

		const hasAccessories = !!dom.accessoryContainer?.querySelector('[data-placed-accessory]');
		const hasContent = hasDrawing || hasAccessories;

		dom.undoButton?.classList.toggle('is-active', hasContent);
		dom.clearButton?.classList.toggle('is-active', hasContent);
		dom.startOverButton?.classList.toggle('is-active', hasContent);
		dom.shareButton?.classList.toggle('is-active', hasContent);
		dom.optionsContainer?.classList.toggle('is-active', hasContent);

		return hasContent;
	};

	/**
	 * Wire up hair/colour/accessories UI and bootstrap sub-modules.
	 */
	const startGame = () => {
		const { pushState } = initUndo({ dom, checkCanvasHasContent, resetToDrawingMode });

		const { deactivateCursorAccessory } = initAccessories({
			dom,
			state,
			checkCanvasHasContent,
			pushState,
		});

		initCanvas({
			dom,
			state,
			checkCanvasHasContent,
			pushState,
			deactivateCursorAccessory,
		});

		initShareModal({ dom, wrapper });

		if (dom.startPoint && dom.pointingHand) {
			dom.startPoint.addEventListener(
				'pointerdown',
				() => {
					dom.pointingHand.classList.remove('is-visible');
				},
				{ once: true },
			);
		}

		dom.startOverButton?.addEventListener('click', () => {
			window.location.reload();
		});

		dom.hairOption?.addEventListener('click', (e) => {
			deactivateCursorAccessory?.();
			resetToDrawingMode();

			if (dom.drawingOptionsList) {
				dom.drawingOptionsList.classList.toggle(
					'wp-block-mavis-design-a-hairstyle__game-drawing-options--hidden',
				);
			}

			dom.colourOptionsList?.classList.add(
				'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
			);

			e.stopPropagation();
		});

		dom.colourOption?.addEventListener('click', () => {
			deactivateCursorAccessory?.();
			state.currentMode = 'coloring';
			updateCursor();

			dom.colourOption?.classList.add('is-active');
			dom.hairOption?.classList.remove('is-active');
			dom.accessoriesOption?.classList.remove('is-active');

			dom.colourOptionsList?.classList.toggle(
				'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
			);

			dom.drawingOptionsList?.classList.add(
				'wp-block-mavis-design-a-hairstyle__game-drawing-options--hidden',
			);
		});

		dom.accessoriesOption?.addEventListener('click', () => {
			deactivateCursorAccessory?.();
			state.currentMode = 'accessorizing';
			updateCursor();
			state.selectedAccessory = null;

			dom.accessoriesOption?.classList.add('is-active');
			dom.hairOption?.classList.remove('is-active');
			dom.colourOption?.classList.remove('is-active');

			dom.colourOptionsList?.classList.add(
				'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
			);
			dom.drawingOptionsList?.classList.add(
				'wp-block-mavis-design-a-hairstyle__game-drawing-options--hidden',
			);
		});

		dom.colourItems?.forEach((item) => {
			item.addEventListener('click', (ev) => {
				ev.stopPropagation();
				state.drawingColor = item.style.backgroundColor || '#000000';
				state.currentMode = 'coloring';
				updateCursor();
				dom.colourItems.forEach((c) => c.classList.remove('is-active'));
				item.classList.add('is-active');

				dom.colourOptionsList?.classList.add(
					'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
				);
			});
		});

		if (dom.drawingItems && dom.drawingItems.length) {
			dom.drawingItems.forEach((item) => {
				item.addEventListener('click', (ev) => {
					ev.stopPropagation();

					const tileType = item.dataset.tile;
					if (tileType === 'primary') {
						state.drawingTile = wrapper.dataset.drawingTile || '';
					} else if (tileType === 'secondary') {
						state.drawingTile = wrapper.dataset.secondDrawingTile || '';
					}
					dom.drawingItems.forEach((i) => i.classList.remove('is-active'));
					item.classList.add('is-active');

					dom.drawingOptionsList?.classList.add(
						'wp-block-mavis-design-a-hairstyle__game-drawing-options--hidden',
					);

					state.currentMode = 'drawing';
					updateCursor();
				});
			});
		}

		document.addEventListener('click', (ev) => {
			if (dom.optionsContainer && dom.optionsContainer.contains(ev.target)) return;

			dom.colourOptionsList?.classList.add(
				'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
			);
			dom.drawingOptionsList?.classList.add(
				'wp-block-mavis-design-a-hairstyle__game-drawing-options--hidden',
			);
		});

		dom.accessoryButtons?.forEach((btn) => {
			btn.addEventListener('click', () => {
				dom.accessoryButtons.forEach((b) => b.classList.remove('is-active'));
				btn.classList.add('is-active');
			});
		});

		dom.clearButton?.addEventListener('click', () => {
			if (typeof pushState === 'function') {
				pushState();
			}
			window.location.reload();
		});

		resetToDrawingMode();

		if (state.drawingTile && dom.drawingItems && dom.drawingItems.length) {
			dom.drawingItems.forEach((item) => {
				const tileType = item.dataset.tile;
				const url =
					tileType === 'primary' ? wrapper.dataset.drawingTile : wrapper.dataset.secondDrawingTile;
				if (url && state.drawingTile === url) {
					item.classList.add('is-active');
				}
			});
		}
	};

	if (dom.headChoices && dom.headChoices.length > 0) {
		dom.headChoices.forEach((choice) => {
			choice.addEventListener('click', () => {
				const img = choice.querySelector('img');
				if (!img) return;

				dom.headContainer.insertAdjacentHTML(
					'afterbegin',
					`<img src="${img.src}" alt="${img.alt}"
			class="wp-block-mavis-design-a-hairstyle__head-image"
			usemap="#wp-block-mavis-design-a-hairstyle__map">`,
				);

				if (dom.headSelection) {
					dom.headSelection.style.display = 'none';
				}

				/**
				 * Unhides the pointing hand.
				 */
				const onTutorialComplete = () => {
					if (dom.pointingHand) dom.pointingHand.classList.add('is-visible');
				};
				displayTutorial(onTutorialComplete);

				startGame();
			});
		});
	} else {
		const singleHeadImage = dom.headContainer?.querySelector(
			'.wp-block-mavis-design-a-hairstyle__head-image',
		);

		/**
		 * Unhides the pointing hand.
		 */
		const onTutorialComplete = () => {
			if (singleHeadImage && dom.pointingHand) {
				dom.pointingHand.classList.add('is-visible');
			}
		};

		displayTutorial(onTutorialComplete);
		startGame();
	}
});
